import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks/data-fetching';
import { t } from '@/i18n';
import { useMessage } from '@/providers';
import { SnapshotModel, SnapshotsModel } from '../_model/snapshot';
import {
  snapshotInvalidateListMap,
  snapshotQueryKeyMap,
} from '../_utils/query-keys';

const BASE_URL = '/api/snapshots/';

type GetSnapshotsParams = {
  referenceFiscalYear?: string;
};

export const useSnapshots = ({
  params,
  enabled,
  options,
}: {
  params?: GetSnapshotsParams;
  enabled?: boolean;
  options?: { onlySuccess?: boolean };
} = {}) => {
  return useQuery({
    queryKey: snapshotQueryKeyMap.snapshots(params),
    queryFn: async () => {
      const response = await axiosGet(BASE_URL, params);
      return SnapshotsModel.parse(response);
    },
    select: (data) => {
      if (options?.onlySuccess) {
        return data.filter((item) => item.status === 'success');
      }
      return data;
    },
    gcTime: 60 * 60 * 1 * 1000, // 12時間
    staleTime: 60 * 60 * 1 * 1000, // 12時間
    enabled,
  });
};

export const useSnapshot = ({
  id,
}: {
  id: string | null | undefined;
}) => {
  return useQuery({
    queryKey: snapshotQueryKeyMap.snapshot(id),
    queryFn: async () => {
      const response = await axiosGet(`${BASE_URL}${id}/`);
      return SnapshotModel.parse(response);
    },
    gcTime: 60 * 60 * 1 * 1000, // 12時間
    staleTime: 60 * 60 * 1 * 1000, // 12時間
    enabled: !!id,
  });
};

export type SnapshotRequestPayloadType = {
  name: string;
  referenceDate: string;
  startYear: number | null;
  endYear: number | null;
};

export const useCreateSnapshot = () => {
  const { invalidate } = useInvalidateQuery();
  const { openMessage } = useMessage();

  return useMutation({
    mutationFn: (params: SnapshotRequestPayloadType) => {
      return axiosPost(BASE_URL, params);
    },
    onSuccess: async () => {
      await Promise.all(
        snapshotInvalidateListMap.updateList().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
      );
      openMessage('success', t('message-create-completed'));
    },
    onError: async () => {
      openMessage('error', t('message-create-failed'));
    },
  });
};

export const useUpdateSnapshot = () => {
  const { invalidate } = useInvalidateQuery();
  const { openMessage } = useMessage();

  return useMutation({
    mutationFn: async (params: { id: string } & SnapshotRequestPayloadType) => {
      const { id, ...payload } = params;
      return await axiosPatch(`${BASE_URL}${id}/`, payload);
    },
    onSuccess: async () => {
      await Promise.all(
        snapshotInvalidateListMap.updateList().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
      );
      openMessage('success', t('message-update-completed'));
    },
    onError: async () => {
      openMessage('error', t('message-update-failed'));
    },
  });
};

export const useDeleteSnapshot = () => {
  const { invalidate } = useInvalidateQuery();
  const { openMessage } = useMessage();

  return useMutation({
    mutationFn: async (id: string) => {
      await axiosDelete(`${BASE_URL}${id}/`);
    },
    onSuccess: async () => {
      await Promise.all(
        snapshotInvalidateListMap.updateList().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
      );
      openMessage('success', t('message-delete-completed'));
    },
    onError: async () => {
      openMessage('error', t('message-delete-failed'));
    },
  });
};
